import type { PolygonBounds } from "../getPolygonBounds";
import type { FindZoneLabelRoom, MeasuredRoom } from "./types";

const areWithin = (a: PolygonBounds, b: PolygonBounds, distance: number) =>
  a.minX <= b.maxX + distance &&
  b.minX <= a.maxX + distance &&
  a.minY <= b.maxY + distance &&
  b.minY <= a.maxY + distance;

// Rooms whose boxes come within one wall of each other form one block (a zone split by another zone has several).
const splitIntoBlocks = (
  rooms: MeasuredRoom[],
  touchDistancePt: number,
): MeasuredRoom[][] => {
  const blockIndexByRoom = new Array<number>(rooms.length).fill(-1);
  const blocks: MeasuredRoom[][] = [];

  for (let seed = 0; seed < rooms.length; seed++) {
    if (blockIndexByRoom[seed] !== -1) continue;
    const block: MeasuredRoom[] = [];
    const pending = [seed];
    blockIndexByRoom[seed] = blocks.length;
    while (pending.length > 0) {
      const index = pending.pop();
      if (index === undefined) break;
      block.push(rooms[index]);
      for (let other = 0; other < rooms.length; other++) {
        if (blockIndexByRoom[other] !== -1) continue;
        if (
          !areWithin(rooms[index].bounds, rooms[other].bounds, touchDistancePt)
        ) {
          continue;
        }
        blockIndexByRoom[other] = blocks.length;
        pending.push(other);
      }
    }
    blocks.push(block);
  }

  return blocks;
};

export const findZoneLabelRoom: FindZoneLabelRoom = ({
  rooms,
  touchDistancePt,
}) => {
  // One pass over the blocks: the biggest block by total area, and the biggest room within it.
  let labelRoom = rooms[0];
  let largestBlockArea = -Infinity;
  for (const block of splitIntoBlocks(rooms, touchDistancePt)) {
    let blockArea = 0;
    let largestRoom = block[0];
    for (const measured of block) {
      blockArea += measured.area;
      if (measured.area > largestRoom.area) largestRoom = measured;
    }
    if (blockArea > largestBlockArea) {
      largestBlockArea = blockArea;
      labelRoom = largestRoom;
    }
  }
  return labelRoom;
};
